import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Shield, Lock, AlertTriangle, Database, Wifi, Smartphone } from "lucide-react";
import {getImageUrl} from '@/lib/utils';

const CyberProtection = () => {
  const pageData = {
    // SEO Meta Data
    title: "Cyber Insurance UAE | Data Breach & Cyber Security Coverage | Yalla Compare",
    metaDescription: "Protect your UAE business from cyber threats with comprehensive cyber insurance. Coverage for data breaches, ransomware, and cyber attacks. Get expert cyber protection.",

    // Hero Section
    heroTitle: "Advanced Cyber Protection for UAE Businesses",
    heroSubtitle: "Cyber Security Insurance",
    heroDescription: "Safeguard your business from evolving cyber threats with comprehensive cyber insurance. From data breaches to ransomware attacks, we provide complete digital protection.",
    heroImage: getImageUrl("cyber-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Protection against data breaches and cyber attacks",
      "Coverage for ransomware and malware incidents",
      "Business interruption due to cyber events",
      "Legal and regulatory compliance support",
      "24/7 cyber incident response team",
      "Reputation management and crisis communication"
    ],

    // Features Section
    features: [
      {
        title: "Data Breach Response",
        description: "Comprehensive support for data breach incidents including notification, credit monitoring, and legal compliance.",
        icon: <Database className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Ransomware Protection",
        description: "Coverage for ransomware attacks including negotiation, payment, and system restoration costs.",
        icon: <Lock className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Business Interruption",
        description: "Financial protection when cyber incidents disrupt your business operations and revenue streams.",
        icon: <Wifi className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Cyber Liability",
        description: "Protection against third-party claims arising from cyber incidents affecting customers or partners.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Incident Response",
        description: "Access to cyber security experts and forensic investigators for immediate incident response.",
        icon: <AlertTriangle className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Mobile Security",
        description: "Coverage for mobile device security breaches and bring-your-own-device (BYOD) risks.",
        icon: <Smartphone className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "First-Party Cyber Coverage",
        description: "Direct costs and losses to your business from cyber incidents",
        included: [
          "Data restoration and recovery costs",
          "Business interruption losses",
          "Cyber extortion and ransomware",
          "Notification and credit monitoring",
          "Forensic investigation expenses",
          "Public relations and crisis management",
          "Regulatory fines and penalties",
          "System damage and replacement"
        ]
      },
      {
        title: "Third-Party Cyber Liability",
        description: "Claims and lawsuits from others affected by your cyber incident",
        included: [
          "Privacy liability claims",
          "Network security liability",
          "Regulatory investigations",
          "Payment card industry fines",
          "Media liability coverage",
          "Intellectual property claims",
          "Defense costs and settlements",
          "Breach of contract claims"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "Cyber Expertise",
        description: "Specialized knowledge of emerging cyber threats and the latest protection strategies for UAE businesses."
      },
      {
        title: "Rapid Response",
        description: "24/7 incident response team with cyber security experts ready to assist during critical situations."
      },
      {
        title: "Comprehensive Coverage",
        description: "All-in-one cyber protection covering both first-party losses and third-party liability claims."
      },
      {
        title: "Risk Prevention",
        description: "Proactive risk assessment and security training to help prevent cyber incidents before they occur."
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "What types of cyber attacks are covered by cyber insurance?",
        answer: "Our cyber insurance covers various attacks including ransomware, malware, phishing, data breaches, denial of service attacks, and social engineering fraud."
      },
      {
        question: "How quickly can I get help if my business suffers a cyber attack?",
        answer: "Our 24/7 incident response team can be activated immediately. Initial response typically begins within 1-2 hours of notification."
      },
      {
        question: "Does cyber insurance cover regulatory fines and penalties?",
        answer: "Yes, our policies include coverage for regulatory fines and penalties resulting from data breaches, subject to local laws and policy terms."
      },
      {
        question: "Is cyber insurance necessary for small businesses in the UAE?",
        answer: "Absolutely. Small businesses are increasingly targeted by cybercriminals and often lack the resources to recover from attacks without insurance protection."
      }
    ],

    // CTA Section
    ctaTitle: "Secure Your Digital Assets Today",
    ctaDescription: "Don't wait for a cyber attack to realize you need protection. Get comprehensive cyber insurance tailored to your UAE business needs.",
    primaryCta: "Get Cyber Insurance Quote",
    secondaryCta: "Assess Your Cyber Risk",

    // Product ID for quote form
    productId: "cyber-insurance"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default CyberProtection;
